import { useEffect, useState } from "react";
import { Modal, Row, Col, Image, Spinner, Accordion } from "react-bootstrap";
import moment from "moment";
import { getBookingDetailAPI } from "../../services/Networkcall";
import { errorAlert } from "../common/Alert";
import { IoCarSport } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const BookingDetails = ({ show, closeModal, bookingId }) => {
    const [booking, setBooking] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleEdit = () => {
        navigate("/reserve-booking", {
            state: {
                editMode: true,
                bookingData: booking
            }
        });
    };


    // ================= FETCH BOOKING DETAIL =================
    const fetchBookingDetail = async () => {
        setLoading(true);
        try {
            const res = await getBookingDetailAPI(bookingId);
            if (res?.success) {
                const normalizeBooking = (data) => {
                    return {
                        ...data,

                        driver_info: data?.driver_info || data?.driver || null,

                        vehicle_info: data?.vehicle_info || data?.vehicle || null,

                        location_info: data?.location_info || data?.location || null,

                        booking_dates: data?.booking_dates || data?.dates || [],

                        bookingType: data?.bookingType || data?.booking_type || "",
                    };
                };

                setBooking(normalizeBooking(res?.data));
            } else {
                errorAlert({ message: res?.message });
            }
        } catch (err) {
            errorAlert({
                message: err?.response?.data?.message || err?.message || "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    // ================= EFFECT =================
    useEffect(() => {
        if (show && bookingId) {
            fetchBookingDetail();
        }
    }, [show, bookingId]);

    return (
        <Modal show={show} onHide={closeModal} centered backdrop="static" size="xl">
            {/* HEADER */}
            <Modal.Header closeButton>
                <h5 className="fw-bold w-100 text-center mb-0">Booking Details</h5>
            </Modal.Header>

            <Modal.Body style={{ height: "75vh", overflow: "hidden" }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Row className="h-100">

                        {/* ================= LEFT SIDE (ACCORDION) ================= */}
                        <Col md={5} className="border-end">
                            <div className="d-flex justify-content-end">
                                {booking?._id && (
                                <button
                                    className="btn btn-sm btn-outline-primary m-2 mt-0"
                                    onClick={() => handleEdit("driver")}
                                >
                                    Edit
                                </button>
                            )}
                            </div>
                            <Accordion defaultActiveKey="0" className="mb-3">

                                <Accordion.Item eventKey="0" className="border-0">

                                    {/* HEADER */}
                                    <Accordion.Header>
                                        <div className="d-flex justify-content-between w-100 align-items-center">
                                            <span className="fw-semibold">Driver Details</span>
                                            {/* selected name (optional) */}
                                            {booking?.driver_info && (
                                                <span className="text-success small">
                                                    {booking.driver_info.userName}
                                                </span>
                                            )}
                                        </div>
                                    </Accordion.Header>

                                    {/* BODY */}
                                    <Accordion.Body>

                                        {booking?.driver_info ? (
                                            <div className="d-flex align-items-center gap-3">

                                                <Image
                                                    src={booking?.driver_info?.profileImage || "assets/images/placeholder.png"}
                                                    width={50}
                                                    height={50}
                                                    className="rounded border"
                                                />

                                                <div>
                                                    <h6 className="mb-1">{booking?.driver_info?.userName}</h6>
                                                    <p className="mb-0 small">{booking?.driver_info?.email}</p>
                                                    <p className="mb-0 small">{booking?.driver_info?.phoneNumber}</p>
                                                </div>

                                            </div>
                                        ) : (
                                            <p className="text-muted small mb-0">No driver assigned</p>
                                        )}

                                    </Accordion.Body>

                                </Accordion.Item>

                            </Accordion>

                            <Accordion defaultActiveKey="0" className="mb-3">

                                <Accordion.Item eventKey="0" className="border-0">

                                    <Accordion.Header>
                                        <div className="d-flex justify-content-between w-100">
                                            <span className="fw-semibold">Vehicle Details</span>

                                            {booking?.vehicle_info && (
                                                <span className="text-success small">
                                                    {booking.vehicle_info.vehicleName}
                                                </span>
                                            )}
                                        </div>
                                    </Accordion.Header>

                                    <Accordion.Body>

                                        {booking?.vehicle_info ? (
                                            <div className="d-flex align-items-center gap-3">

                                                <div className="icon-box">
                                                    <IoCarSport size={28} color="#2563eb" />
                                                </div>

                                                <div>
                                                    <h6 className="mb-1">
                                                        {booking?.vehicle_info?.vehicleName}
                                                    </h6>

                                                    <p className="mb-0 small">
                                                        {booking?.vehicle_info?.licensePlate}
                                                    </p>
                                                </div>

                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0">
                                                No vehicle assigned
                                            </p>
                                        )}

                                    </Accordion.Body>

                                </Accordion.Item>

                            </Accordion>

                        </Col>

                        {/* ================= RIGHT SIDE (DETAILS) ================= */}
                        <Col md={7} className="h-100">

                            <div style={{ height: "100%", overflowY: "auto", paddingLeft: "10px" }}>

                                {/* LOCATION */}
                                <div className="bg-light rounded-4 p-3 mb-4">
                                    <div className="d-flex align-items-center gap-3">
                                        <Image
                                            src={booking?.location_info?.locationImage || "assets/images/imageplaceholder.png"}
                                            width={70}
                                            height={70}
                                            className="object-fit-contain"
                                        />
                                        <div>
                                            <h5 className="fw-bold mb-1">
                                                {booking?.location_info?.locationName}
                                            </h5>
                                            <p className="mb-0 fw-semibold">
                                                {booking?.location_info?.locationAddress}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* BOOKING DATES */}
                                <h5 className="fw-semibold mb-3">Booking Dates</h5>

                                <div className="bg-light rounded-4 p-4 mb-4">
                                    {booking?.booking_dates?.map((date, idx) => (
                                        <div key={idx} className="mb-2">
                                            <div className="d-flex gap-3">
                                                <small className="text-secondary fw-semibold">Arrive After</small>
                                                <small>
                                                    {moment(date?.startDateTime)
                                                        .tz(booking?.location_info?.location_timezone || moment.tz.guess())
                                                        .format("DD MMM YYYY hh:mm A")}
                                                </small>
                                            </div>

                                            <div className="d-flex gap-3">
                                                <small className="text-secondary fw-semibold">Exit Before</small>
                                                <small>
                                                    {moment(date?.endDateTime)
                                                        .tz(booking?.location_info?.location_timezone || moment.tz.guess())
                                                        .format("DD MMM YYYY hh:mm A")}
                                                </small>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* BOOKING DETAIL */}
                                <h5 className="fw-bold mb-3">Booking Detail</h5>

                                <div className="bg-light rounded-4 p-4 mb-4">

                                    <div className="d-flex justify-content-between small">
                                        <span>Access Pin</span>
                                        <span>{booking?.uniFiAccessPin || "-"}</span>
                                    </div>

                                    <div className="d-flex justify-content-between small">
                                        <span>Booking Date</span>
                                        <span>{moment(booking?.createdAt).format("DD MMM YYYY")}</span>
                                    </div>

                                    <div className="d-flex justify-content-between small">
                                        <span>Duration</span>
                                        <span>{booking?.bookingType}</span>
                                    </div>

                                    <div className="d-flex justify-content-between small">
                                        <span>Price</span>
                                        <span className="text-success fw-bold">
                                            ${booking?.parkingBaseAmount || 0}
                                        </span>
                                    </div>

                                </div>

                                {/* SUBTOTAL */}
                                <div className="bg-light rounded-4 p-4 d-flex justify-content-between">
                                    <h5 className="mb-0">Sub Total</h5>
                                    <h4 className="text-success mb-0">
                                        ${booking?.totalAmount || 0}
                                    </h4>
                                </div>

                            </div>
                        </Col>

                    </Row>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default BookingDetails;
